// Red messages clear as soon as a field is fixed (every form): when someone edits a field that is marked red and it
// now has a value, its red box and its own message go straight away; the message at the top of the form goes when it
// was about that field, or when no red box is left in the form. Nothing waits for the next save.

/** The ways Falcon's forms mark a field red: on the field itself, or on the box around it. */
const FIELD_MARKS = ["field-error", "invalid", "is-invalid"];
const BOX_MARKS = ["is-bad", "is-error", "has-error"];
/** The small red message a box shows under its field. */
const BOX_MESSAGES = ".cl-err, .sw-err, .ax-err, .field-msg, .qcw-err-text, .field-error-msg";
/** The message at the top of a form. */
const TOP_MESSAGES = ".form-message, .cl-msg";

const filled = (el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) =>
  el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "radio") ? true : String(el.value ?? "").trim() !== "";

/** Is anything in this form still marked red (a field or the box around a field)? */
function stillRed(form: HTMLElement): boolean {
  if (form.querySelector(FIELD_MARKS.map((c) => `.${c}`).join(", "))) return true;
  return [...form.querySelectorAll<HTMLElement>(BOX_MARKS.map((c) => `.${c}`).join(", "))].some((b) => b.querySelector("input, select, textarea"));
}

export function fieldFixed(target: EventTarget | null): void {
  const el = target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
  if (!el || !(el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement)) return;
  const form = el.closest<HTMLElement>("form") || el.closest<HTMLElement>(".modal, #modal");
  const box = form ? [...BOX_MARKS].map((c) => el.closest<HTMLElement>(`.${c}`)).find((b) => b && form.contains(b)) || null : null;
  const marked = FIELD_MARKS.some((c) => el.classList.contains(c)) || !!box;
  const top = form?.querySelector<HTMLElement>(TOP_MESSAGES);
  const tied = !!top && !top.hidden && !!el.name && top.dataset.field === el.name;
  if ((!marked && !tied) || !filled(el)) return;
  el.classList.remove(...FIELD_MARKS);
  el.removeAttribute("aria-invalid");
  if (box) {
    box.classList.remove(...BOX_MARKS);
    box.querySelectorAll(BOX_MESSAGES).forEach((m) => m.remove());
  }
  if (form && top && !top.hidden && (tied || !stillRed(form))) {
    top.hidden = true;
    delete top.dataset.field;
  }
}

document.addEventListener("input", (e) => fieldFixed(e.target), true);
document.addEventListener("change", (e) => fieldFixed(e.target), true);
